// API Service Layer
import API_BASE_URL, { apiCall, API_ENDPOINTS } from '../config/api.js';
import DetectionAPI from './detectionApi';

class ApiService {
  // Health checks
  static async checkHealth() {
    return await apiCall(API_ENDPOINTS.HEALTH);
  }

  static async getDetailedHealth() {
    return await apiCall(API_ENDPOINTS.HEALTH_DETAILED);
  }

  // Detection and Analysis APIs
  static async detectProducts(files, settings = {}) {
    return DetectionAPI.detectProducts(files, {
      detect_confidence: 0.25,
      filter_min_confidence: 0.40,
      iou_threshold: 0.45,
      ...settings
    });
  }

  static async getAvailableModels() {
    return await apiCall('/api/detection/models');
  }

  static async analyzeBatch(files, apiKey, language = 'arabic') {
    return DetectionAPI.analyzeBatch(files, apiKey, language);
  }

  static async getAnalysisStatus(taskId) {
    return DetectionAPI.getAnalysisStatus(taskId);
  }

  static async getAnalysisResults(taskId) {
    return DetectionAPI.getAnalysisResults(taskId);
  }

  static async exportProducts(filters = {}) {
    return DetectionAPI.exportProducts(filters);
  }

  // Offers
  static async getAllOffers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `${API_ENDPOINTS.OFFERS}?${queryString}` : API_ENDPOINTS.OFFERS;
    return apiCall(endpoint);
  }

  static async processOffer(offerId, files) {
    const formData = new FormData();
    // إزالة offer_id من FormData وإضافته للـ URL
    
    files.forEach(file => {
      formData.append('files', file);
    });

    return apiCall(`${API_ENDPOINTS.OFFERS_PROCESS}?offer_id=${offerId}`, {
      method: 'POST',
      headers: {}, // Remove Content-Type to let browser set it for FormData
      body: formData,
    });
  }

  static async uploadOfferFiles(offerId, files) {
    const formData = new FormData();
    // إزالة offer_id من FormData وإضافته للـ URL
    files.forEach(file => {
      formData.append('files', file);
    });
    return apiCall(`${API_ENDPOINTS.OFFERS_PROCESS}?offer_id=${offerId}`, {
      method: 'POST',
      headers: {}, // اترك المتصفح يحدد Content-Type
      body: formData,
    });
  }

  static async getProcessingStatus(jobId) {
    return apiCall(API_ENDPOINTS.OFFERS_STATUS(jobId));
  }

  static async getProcessingResults(jobId) {
    return apiCall(API_ENDPOINTS.OFFERS_RESULTS(jobId));
  }

  static async createOffer(data) {
    return apiCall(API_ENDPOINTS.OFFERS_NEW, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateOfferProducts(offerId, products) {
    return apiCall(`/api/offers/${offerId}/products`, {
      method: 'POST',
      body: JSON.stringify({ products }),
    });
  }

  static async publishOffer(offerId) {
    return apiCall(`/api/offers/${offerId}/publish`, {
      method: 'POST',
    });
  }

  static async startOfferProcessing(offerId) {
    return apiCall(`/api/offers/${offerId}/process`, {
      method: 'POST',
    });
  }

  static async getOfferProducts(offerId) {
    return apiCall(`/api/offers/${offerId}/products`);
  }

  static async getOfferSummary(offerId) {
    return apiCall(`/api/offers/${offerId}/summary`);
  }

  static async getOfferDetails(offerId) {
    return apiCall(`/api/offers/${offerId}`);
  }

  static async updateOffer(offerId, data) {
    return apiCall(`/api/offers/${offerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteOffer(offerId) {
    return apiCall(`/api/offers/${offerId}`, {
      method: 'DELETE',
    });
  }

  // Products
  static async getProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `${API_ENDPOINTS.PRODUCTS}?${queryString}` : API_ENDPOINTS.PRODUCTS;
    return apiCall(endpoint);
  }

  static async bulkProductAction(productIds, action) {
    return apiCall(API_ENDPOINTS.PRODUCTS_BULK, {
      method: 'POST',
      body: JSON.stringify({
        product_ids: productIds,
        action: action
      }),
    });
  }

  // Merchants
  static async getMerchants(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `${API_ENDPOINTS.MERCHANTS}?${queryString}` : API_ENDPOINTS.MERCHANTS;
    return apiCall(endpoint);
  }

  static async getMerchant(merchantId) {
    return apiCall(`${API_ENDPOINTS.MERCHANTS}/${merchantId}`);
  }

  static async createMerchant(data) {
    return apiCall(API_ENDPOINTS.MERCHANTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateMerchant(merchantId, data) {
    return apiCall(`${API_ENDPOINTS.MERCHANTS}${merchantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteMerchant(merchantId) {
    return apiCall(`${API_ENDPOINTS.MERCHANTS}/${merchantId}`, {
      method: 'DELETE',
    });
  }

  static async getMerchantBranches(merchantId) {
    const endpoint = `/api/merchants/${merchantId}/branches`;
    return apiCall(endpoint);
  }

  static async getCities() {
    return apiCall(API_ENDPOINTS.CITIES);
  }

  static async getCity(cityId) {
    return apiCall(`${API_ENDPOINTS.CITIES}/${cityId}`);
  }

  static async createCity(data) {
    return apiCall(API_ENDPOINTS.CITIES, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateCity(cityId, data) {
    return apiCall(`${API_ENDPOINTS.CITIES}${cityId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteCity(cityId) {
    return apiCall(`${API_ENDPOINTS.CITIES}/${cityId}`, {
      method: 'DELETE',
    });
  }

  // Auth
  static async login(email, password) {
    return apiCall(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  static async logout() {
    return apiCall(API_ENDPOINTS.LOGOUT, {
      method: 'POST',
    });
  }

  static async getCurrentUser() {
    return apiCall(API_ENDPOINTS.ME);
  }

  // Export data
  static async exportStats(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/api/export/stats?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    return response;
  }
}

/**
 * Check database status
 */
export const checkDatabaseStatus = async () => {
  try {
    const response = await apiCall('/api/health/database-status');
    return response;
  } catch (error) {
    console.error('Failed to check database status:', error);
    throw error;
  }
};

/**
 * Setup database with sample data
 */
export const setupDatabase = async () => {
  try {
    const response = await apiCall('/api/health/setup-database', {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Failed to setup database:', error);
    throw error;
  }
};

export default ApiService;